import React, { useState } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';

interface ActionObjectPair {
  action: string;
  object: string;
  modifier?: string;
  structure?: string;
  measurement?: {
    value: number;
    unit: string;
  };
}

interface ClassificationResult {
  intents: ActionObjectPair[];
  conversationTurnId?: number;
}

interface TextClassifierProps {
  sessionId: string;
  provider: 'openai' | 'ollama';
  ollamaModel: string;
  openaiModel: string;
}

const TextClassifier: React.FC<TextClassifierProps> = ({ sessionId, provider, ollamaModel, openaiModel }) => {
  const [text, setText] = useState('');
  const [context, setContext] = useState('');
  const [results, setResults] = useState<ClassificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackRating, setFeedbackRating] = useState<'correct' | 'incorrect' | 'mixed' | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.classifyText(
        text, 
        sessionId, 
        context, 
        provider, 
        provider === 'ollama' ? ollamaModel : openaiModel
      );
      setResults(response);

      // Reset feedback state for new classification
      setFeedbackRating(null);
      setFeedbackComment('');
      setFeedbackSubmitted(false);

      // Clear input after successful classification
      setText('');
      setContext('');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to classify text. Please try again.';
      setError(errorMessage);
      logger.error('Classification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setText('');
    setContext('');
    setResults(null);
    setError(null);
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackSubmitted(false);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackRating || !results?.conversationTurnId) return;

    setFeedbackLoading(true);
    try {
      await apiService.submitFeedback(
        results.conversationTurnId,
        feedbackRating,
        feedbackComment.trim() || undefined
      );
      setFeedbackSubmitted(true);
      logger.info('Feedback submitted successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to submit feedback. Please try again.';
      setError(errorMessage);
      logger.error('Feedback submission error:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Text Intent Classification
          </h2>
          <div className="text-sm text-gray-600">
            Using: <span className="font-semibold text-blue-600">{provider === 'openai' ? 'OpenAI' : 'Ollama'}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
              Enter text to classify:
            </label>
            <textarea
              id="text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
              placeholder="e.g., 'Play some music and order a pizza'"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="context-input" className="block text-sm font-medium text-gray-700 mb-2">
              Enter context:
            </label>
            <textarea
              id="context-input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={2}
              placeholder="e.g., 'User is in a car, driving to work'"
              disabled={loading}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !text.trim()}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Classifying...' : 'Classify Intent'}
            </button>
            
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Classification Results:
            </h3>
            
            {results.intents.length > 0 ? (
              <div className="space-y-4">
                {results.intents.map((pair, index) => (
                  <div key={index} className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <h4 className="text-md font-semibold text-gray-800 mb-2">
                      Action-Object Pair {results.intents.length > 1 ? `#${index + 1}` : ''}:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Action:</span>
                        <div className="ml-2">
                          <span className="text-sm font-bold text-green-800">
                            {pair.action}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Object:</span>
                        <div className="ml-2">
                          <span className="text-sm font-bold text-green-800">
                            {pair.object}
                          </span>
                        </div>
                      </div>
                      {pair.modifier && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Modifier:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                              {pair.modifier}
                            </span>
                          </div>
                        </div>
                      )}
                      {pair.structure && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Structure:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-sm rounded-full font-semibold">
                              {pair.structure}
                            </span>
                          </div>
                        </div>
                      )}
                      {pair.measurement && (
                        <div>
                          <span className="text-sm font-medium text-gray-600">Measurement:</span>
                          <div className="ml-2">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-semibold">
                              {pair.measurement.value}{pair.measurement.unit ? ` ${pair.measurement.unit}` : ''}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-600">
                  No valid action-object pairs were identified in the provided text.
                </p>
              </div>
            )}

            {/* Feedback Section */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="text-md font-semibold text-gray-800 mb-3">
                Feedback
              </h4>
              
              {feedbackSubmitted ? (
                <div className="p-3 bg-green-100 border border-green-300 rounded-md">
                  <p className="text-sm text-green-800 font-medium">
                    ✓ Thank you for your feedback!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rate this classification:
                    </label>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="feedback"
                          value="correct"
                          checked={feedbackRating === 'correct'}
                          onChange={(e) => setFeedbackRating('correct')}
                          className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-2xl">✅</span>
                        <span className="ml-2 text-sm text-gray-700 font-medium">Correct - All classifications are accurate</span>
                      </label>
                      
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="feedback"
                          value="incorrect"
                          checked={feedbackRating === 'incorrect'}
                          onChange={(e) => setFeedbackRating('incorrect')}
                          className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-2xl">❌</span>
                        <span className="ml-2 text-sm text-gray-700 font-medium">Incorrect - All classifications are wrong</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="feedback"
                          value="mixed"
                          checked={feedbackRating === 'mixed'}
                          onChange={(e) => setFeedbackRating('mixed')}
                          className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-2xl">⚠️</span>
                        <span className="ml-2 text-sm text-gray-700 font-medium">Mixed - Some correct, some incorrect</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="feedback-comment" className="block text-sm font-medium text-gray-700 mb-2">
                      Comment (optional):
                    </label>
                    <textarea
                      id="feedback-comment"
                      value={feedbackComment}
                      onChange={(e) => setFeedbackComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows={3}
                      placeholder="Tell us more about your experience..."
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmitFeedback}
                    disabled={!feedbackRating || feedbackLoading}
                    className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {feedbackLoading ? 'Sending...' : 'Send Feedback'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {!results && text && !loading && !error && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="text-sm text-yellow-700">
              Submit the text above to see classification results.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextClassifier;
